import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type AdhocCall, type CallSummary, type FlowTemplate, type Voice } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";
import { buildQuickMessageFlow } from "./quickMessageFlow";
import { loadLastDestination, saveLastDestination } from "./lastDestinationStorage";
import {
  coerceVariables,
  declaredVariables,
  missingRequired,
  type VariableDecl,
} from "./templateVariables";
import { adviseFlow } from "./flowAdvice";
import { asFlowDocument } from "./flowPlan";
import { FlowPlanDialog } from "./FlowPlanDialog";
import type { FlowDocument } from "./types";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

const POLL_INTERVAL_MS = 1000;

/**
 * Triggers one real outbound call against an already-published flow version
 * (docs/PROGRESS.md Slice AY, P1's "real outbound voice call ... via flow
 * builder"). The API process only records the request — cmd/worker's own
 * poll loop (RunAdhocCallDispatcher) claims it and places the real call, the
 * same durable-state path every other outbound call in this system already
 * goes through. This panel polls the request's own status until the
 * dispatcher has acted, rather than pretending the call happens
 * synchronously.
 *
 * Gated to tenant_admin only (tenant.ActionPlaceAdhocCall) — narrower than
 * ReferencesPanel, which operators can also use — because dialing an
 * arbitrary destination on demand is a more direct toll-fraud exposure than
 * campaign operations, which are bounded by a pre-imported recipient list.
 */
type Mode = "existing" | "quick_message";

/** A template chosen elsewhere ("Place test call" on Templates or after saving). */
function templateFromUrl(): string {
  return new URLSearchParams(window.location.search).get("template") ?? "";
}

export function AdhocCallPanel() {
  const [mode, setMode] = useState<Mode>(() => (templateFromUrl() ? "existing" : "quick_message"));
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [flowVersionId, setFlowVersionId] = useState(templateFromUrl);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voiceKey, setVoiceKey] = useState("");
  const [messageText, setMessageText] = useState("");
  const [amdCheck, setAmdCheck] = useState(false);
  const [voicemailText, setVoicemailText] = useState("");
  const [destination, setDestination] = useState(() => loadLastDestination(window.localStorage));
  const [trunkRef, setTrunkRef] = useState("signalwire-trunk");
  const [callerId, setCallerId] = useState("");
  const [templateVars, setTemplateVars] = useState<VariableDecl[]>([]);
  // The chosen template's own program, kept so the plan dialog and the
  // design advice below can be shown BEFORE the call is placed rather than
  // explained afterwards from a log.
  const [templateDoc, setTemplateDoc] = useState<FlowDocument | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const [request, setRequest] = useState<AdhocCall | null>(null);
  const [callDetail, setCallDetail] = useState<CallSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Saved templates only. Quick messages publish a real flow on every
    // send, and listing those made this dropdown unusable.
    api
      .listTemplates()
      .then(({ templates }) => {
        if (cancelled) return;
        setTemplates(templates);
        const first = templates[0];
        if (first) setFlowVersionId((current) => current || first.latest_version_id);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeError(err));
      });
    api
      .listVoices()
      .then(({ voices }) => {
        if (cancelled) return;
        setVoices(voices);
        const first = voices[0];
        if (first) setVoiceKey((current) => current || first.voice + "\u0000" + first.language);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A template declares the variables it needs; the form asks for exactly
  // those. Without this a required one has no way to be supplied, the run
  // refuses to start and the call hangs up with no audio (Slice DI).
  useEffect(() => {
    if (mode !== "existing" || flowVersionId === "") {
      setTemplateVars([]);
      setTemplateDoc(null);
      return;
    }
    let cancelled = false;
    api
      .getFlowVersion(flowVersionId)
      .then(({ program }) => {
        if (cancelled) return;
        setTemplateVars(declaredVariables(program));
        setTemplateDoc(asFlowDocument(program));
      })
      .catch((err: unknown) => {
        // Not fatal: the call can still be placed, it will just fail the
        // same way it did before if the template needed something.
        if (!cancelled) setError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [mode, flowVersionId]);

  useEffect(() => {
    if (!request || request.status !== "pending") return;
    const id = request.adhoc_call_id;
    const timer = window.setInterval(() => {
      api
        .getAdhocCall(id)
        .then((updated) => setRequest((current) => (current?.adhoc_call_id === id ? updated : current)))
        .catch(() => {
          /* a transient poll failure is not worth surfacing; the next tick retries */
        });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [request]);

  // Once dispatched, poll GET /v1/calls for this call's own amd_result —
  // only meaningful when the quick-message flow actually included an
  // amd_gate node (amdCheck at submit time); NOT_RUN otherwise, same as any
  // other flow without one. Stops once the call itself reaches "terminal".
  useEffect(() => {
    const callId = request?.call_id;
    if (!callId) {
      setCallDetail(null);
      return;
    }
    let cancelled = false;
    let reachedTerminal = false;
    const poll = () => {
      if (cancelled || reachedTerminal) return;
      api
        .listCalls()
        .then(({ calls }) => {
          if (cancelled) return;
          const match = calls.find((c) => c.id === callId);
          if (!match) return;
          setCallDetail(match);
          if (match.status === "terminal") reachedTerminal = true;
        })
        .catch(() => {
          /* a transient poll failure is not worth surfacing; the next tick retries */
        });
    };
    poll();
    const timer = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [request?.call_id]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setRequest(null);
    setCallDetail(null);
    try {
      const csrfToken = readCsrfCookie(document.cookie);
      let targetFlowVersionId = flowVersionId;
      if (mode === "quick_message") {
        const [voice, language] = voiceKey.split("\u0000");
        if (!voice || !language) throw new Error("register a voice first (above) to use a quick message");
        const doc = buildQuickMessageFlow(messageText, voice, language, amdCheck, amdCheck ? voicemailText : "");
        // Marked as what it is, so the Templates list can show saved
        // templates only. Before this every send left one behind.
        const published = await api.publishFlow(
          csrfToken,
          "Quick message " + new Date().toISOString(),
          doc,
          { kind: "quick_message" },
        );
        targetFlowVersionId = published.flow_version_id;
      }
      let variables: Record<string, string | number | boolean> | undefined;
      if (mode === "existing" && templateVars.length > 0) {
        const coerced = coerceVariables(templateVars, varValues);
        if (!coerced.ok) throw new Error(coerced.message);
        variables = coerced.values;
      }
      const placed = await api.placeAdhocCall(
        csrfToken,
        targetFlowVersionId,
        destination,
        trunkRef,
        callerId,
        variables,
      );
      setRequest(placed);
      saveLastDestination(window.localStorage, destination);

    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const missingVars = mode === "existing" ? missingRequired(templateVars, varValues) : [];
  // Advice, not validation: everything flagged here publishes and dials
  // perfectly well (see flowAdvice.ts), so it never gates canSubmit.
  const templateAdvice = templateDoc ? adviseFlow(templateDoc) : [];
  const canSubmit =
    mode === "existing"
      ? flowVersionId !== "" && missingVars.length === 0
      : messageText.trim() !== "" && voiceKey !== "";

  return (
    <section aria-labelledby="adhoc-call-heading" className="card">
      <h2 id="adhoc-call-heading">Place a call</h2>
      <p className="card__note">
        Triggers one real outbound call. cmd/worker's own dispatcher places it within a few
        seconds of the request below — this does not dial synchronously. Quick message builds a
        minimal flow (start → tts → hangup) from your text and a registered voice, publishes it,
        then places the call — the same publish/place path an authored flow already uses.
      </p>

      <div className="field" role="radiogroup" aria-label="Message source">
        <label>
          <input
            type="radio"
            name="adhoc-call-mode"
            checked={mode === "quick_message"}
            onChange={() => setMode("quick_message")}
          />
          Quick message (type text, rendered via TTS)
        </label>
        <label>
          <input
            type="radio"
            name="adhoc-call-mode"
            checked={mode === "existing"}
            onChange={() => setMode("existing")}
          />
          Saved template
        </label>
      </div>

      <form className="adhoc-call__form" onSubmit={(event) => void submit(event)}>
        {mode === "quick_message" ? (
          <>
            <label className="field">
              <span>Message</span>
              <textarea
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                placeholder="What should this call say?"
                required
              />
            </label>
            <label className="field">
              <span>Voice</span>
              <select value={voiceKey} onChange={(event) => setVoiceKey(event.target.value)} required>
                {voices.length === 0 ? <option value="">No voices registered yet (see above)</option> : null}
                {voices.map((v) => (
                  <option key={v.voice + v.language} value={v.voice + "\u0000" + v.language}>
                    {v.voice} ({v.language})
                  </option>
                ))}
              </select>
            </label>
            <label className="field field--checkbox">
              <input type="checkbox" checked={amdCheck} onChange={(event) => setAmdCheck(event.target.checked)} />
              <span>
                Check for answering machine (AMD). The result (human / machine / notsure) is
                recorded either way, viewable after the call.
              </span>
            </label>
            {amdCheck ? (
              <label className="field">
                <span>Voicemail message (optional)</span>
                <textarea
                  value={voicemailText}
                  onChange={(event) => setVoicemailText(event.target.value)}
                  placeholder="Leave blank to play the same message above either way. Fill in to hear a different message when AMD detects a machine — a good way to test whether detection is working."
                />
              </label>
            ) : null}
          </>
        ) : (
          <>
            <label className="field">
              <span>Template</span>
              <select value={flowVersionId} onChange={(event) => setFlowVersionId(event.target.value)} required>
                {templates.length === 0 ? <option value="">No templates saved yet</option> : null}
                {templates.map((t) => (
                  <option key={t.flow_id} value={t.latest_version_id}>
                    {t.name} (v{t.latest_version_number})
                  </option>
                ))}
              </select>
            </label>
            {templateDoc ? (
              <p className="adhoc-call__plan">
                <button type="button" className="btn--outline btn--sm" onClick={() => setPlanOpen(true)}>
                  View call flow plan
                </button>
                {/* The advice is summarised here rather than only inside the
                    dialog: a tester who does not think to open the dialog is
                    exactly the person who needs to know that a branch of
                    this template ends the call in silence. */}
                {templateAdvice.length > 0 ? (
                  <span className="field__warning" role="note">
                    {" "}
                    {templateAdvice.length === 1
                      ? "1 thing worth checking in this flow"
                      : `${templateAdvice.length} things worth checking in this flow`}{" "}
                    — the call will still be placed.
                  </span>
                ) : null}
              </p>
            ) : null}
          </>
        )}
        {mode === "existing" && templateVars.length > 0 ? (
          <fieldset className="field">
            <legend>Template values</legend>
            <p className="card__note">
              This template speaks these values. A required one with no value stops the call
              before it dials, rather than reading the placeholder aloud.
            </p>
            {templateVars.map((v) => (
              <label className="field" key={v.name}>
                <span>
                  {v.name}
                  {v.required ? " (required)" : " (optional)"}
                  {v.type === "string" ? "" : ` — ${v.type}`}
                </span>
                <input
                  type="text"
                  value={varValues[v.name] ?? ""}
                  placeholder={v.type === "boolean" ? "true or false" : v.type === "number" ? "0" : ""}
                  onChange={(event) =>
                    setVarValues((current) => ({ ...current, [v.name]: event.target.value }))
                  }
                />
              </label>
            ))}
          </fieldset>
        ) : null}
        <label className="field">
          <span>Destination (E.164)</span>
          <input
            type="text"
            placeholder="+15550100000"
            value={destination}
            onChange={(event) => setDestination(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Trunk</span>
          <input type="text" value={trunkRef} onChange={(event) => setTrunkRef(event.target.value)} required />
        </label>
        <label className="field">
          <span>Caller ID (optional)</span>
          <input type="text" value={callerId} onChange={(event) => setCallerId(event.target.value)} />
        </label>
        <button type="submit" disabled={submitting || !canSubmit}>
          Place call
        </button>
      </form>

      {error ? (
        <p className="card__error" role="alert">
          {error}
        </p>
      ) : null}

      {request ? (
        <p className="card__note" role="status">
          {request.status === "pending" ? `Request ${request.adhoc_call_id} is pending…` : null}
          {request.status === "dispatched" ? `Dispatched — call id ${request.call_id}` : null}
          {request.status === "failed" ? `Failed: ${request.error}` : null}
          {amdCheck && request.status === "dispatched" ? (
            <>
              {" — AMD: "}
              {callDetail
                ? callDetail.amd_result === "NOT_RUN"
                  ? "running…"
                  : callDetail.amd_result
                : "running…"}
            </>
          ) : null}
        </p>
      ) : null}

      {planOpen && templateDoc ? (
        <FlowPlanDialog
          document={templateDoc}
          title={
            templates.find((t) => t.latest_version_id === flowVersionId)?.name ?? "This template"
          }
          onClose={() => setPlanOpen(false)}
        />
      ) : null}
    </section>
  );
}
